import { AttributeDefinitionDc, AttributeType } from "@evergis/api";
import { ComparisonOperator, RangeValue } from "@evergis/ui";
import { RangeClass, UniqueClass } from "@evergis/react";

import {
  ClassificationAttribute,
  ClassificationClass,
  ClassificationParameterExtended,
  isRangeClass,
  isUniqueClass,
} from "interfaces/classification";
// eslint-disable-next-line max-len
import { CLUSTER_CLASSIFY_FILL_COLOR_ATTRIBUTE } from "../../../components/SymbolEditor/components/ClusterSymbolEditor/consts";
import { createLegendSymbol } from "./createLegendSymbol";
import { formatRangeClassValue, formatUniqueClassValue } from "../classification/formatValue";
import { castResolutionToScale, castScaleToResolution } from "../scale";
import { createConditionString } from "../condition/createCondition";
import { getExtractedSymbol } from "../../../utils/style";
import { StyleProxy } from "../classification/StyleProxy";
import { ExtendedClassification } from "../classification/getClassificationsFromSymbol";
import { isClusterFillColor as checkIsClusterFillColor, StyleSymbolType } from "../../../interfaces/style";
import { FilterCondition } from "../condition/FilterCondition";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";

export type LegendSectionItem = {
  symbol: StyleSymbolType;
  title: string;
  hiddenCondition?: string;
};

export type LegendSectionProps = {
  baseSymbol: StyleSymbolType;
  attributeName: string;
  attributeType: AttributeType;
  sectionTitle: string;
  parameter: ClassificationParameterExtended;
  items: LegendSectionItem[];
};

function createItemTitle(value: ClassificationClass, index: number): string {
  if (value.title) return value.title;

  if (isRangeClass(value)) return formatRangeClassValue(value, index);
  if (isUniqueClass(value)) return formatUniqueClassValue(value);
}

export function createHiddenCondition(attributeName: string, attributeType: AttributeType, value: ClassificationClass) {
  let newValue;
  let comparisonOperator;

  if (isRangeClass(value)) {
    if (value.from && value.to) {
      newValue = [value.from as string, value.to as string];
      comparisonOperator = ComparisonOperator.Outside;
    } else if (value.from) {
      newValue = value.from;
      comparisonOperator = ComparisonOperator.LessThan;
    } else if (value.to) {
      newValue = value.to;
      comparisonOperator = ComparisonOperator.GreaterThan;
    }
  } else {
    newValue = value.uniqueValue;
    comparisonOperator = ComparisonOperator.NotEquals;
  }

  return new FilterCondition(
    createConditionString({
      attribute: attributeName,
      type: attributeType,
      comparisonOperator,
      value: newValue,
    }),
  ).toString();
}

function createLegendSectionItems(
  { values, parameter, attribute }: ExtendedClassification,
  classificationsParams: ClassificationParameterExtended[],
  baseSymbol: StyleSymbolType,
): LegendSectionItem[] {
  return values.map((value, index) => {
    return {
      title: createItemTitle(value, index),
      hiddenCondition: createHiddenCondition(attribute, value.attribute.type, value),
      symbol: createLegendSymbol(value.parameterValue, parameter, classificationsParams, baseSymbol),
    };
  });
}

const createOtherLegendSection = (
  { values, parameter, attribute, defaultTitle, defaultValue }: ExtendedClassification,
  classificationsParams: ClassificationParameterExtended[],
  baseSymbol: StyleSymbolType,
  title?: string,
): LegendSectionItem => {
  const value = values[values.length - 1];
  let hiddenCondition;

  if (isRangeClass(value)) {
    hiddenCondition = new FilterCondition(
      createConditionString({
        attribute,
        type: value.attribute.type,
        comparisonOperator: ComparisonOperator.LessOrEquals,
        value: (value as RangeClass).to || (value as RangeClass).from,
      }),
    ).toString();
  } else {
    const conditions = values.map(val =>
      new FilterCondition(
        createConditionString({
          attribute,
          type: val.attribute.type,
          comparisonOperator: ComparisonOperator.Equals,
          value: (val as UniqueClass).uniqueValue,
        }),
      ).toString(),
    );

    hiddenCondition = `( ${conditions.filter(Boolean).join(` ${FilterConditionOperation.OR} `)} )`;
  }

  if (parameter === "symbol") {
    return {
      title,
      hiddenCondition,
      symbol: baseSymbol,
    };
  }

  return {
    title: defaultTitle,
    hiddenCondition,
    symbol: createLegendSymbol(defaultValue, parameter, classificationsParams, baseSymbol),
  };
};

function setClassificationValuesAttribute(
  classification: ExtendedClassification,
  attribute: ClassificationAttribute,
  excludeIndexes?: number[],
): ExtendedClassification {
  const filtered = !excludeIndexes?.length
    ? classification.values
    : classification.values.filter((item, index) => !excludeIndexes.includes(index));

  return {
    ...classification,
    values: filtered.map(value => ({
      ...value,
      attribute,
    })),
  };
}

export function createLegendSections(
  style: StyleProxy,
  attributes: Record<string, AttributeDefinitionDc>,
  defaultScaleRange?: RangeValue,
  currentResolution?: number,
  title?: string,
): LegendSectionProps[] {
  const { classificationManager, symbol, children } = style;
  const classificationsParams = classificationManager.map(({ parameter }) => parameter);
  const excludeIndexes = [];

  if (children) {
    const filtered = children.find(
      ({ minResolution, maxResolution }) =>
        !maxResolution || (minResolution <= currentResolution && maxResolution > currentResolution),
    );

    const resolution = castScaleToResolution(castResolutionToScale(filtered, defaultScaleRange));

    children.forEach(({ minResolution, maxResolution }, index) => {
      if (maxResolution && minResolution < resolution[0]) {
        excludeIndexes.push(index);
      }
    });
  }

  return classificationManager.reduce((legendSections, rawClassification) => {
    const attributeName = rawClassification.attribute;
    const attribute = attributes[attributeName];

    const attributeType =
      attributeName === CLUSTER_CLASSIFY_FILL_COLOR_ATTRIBUTE ? AttributeType.Int32 : attribute.type;

    const classification = setClassificationValuesAttribute(
      rawClassification,
      { ...(attribute || { type: attributeType }), name: attributeName },
      excludeIndexes,
    );

    const isClusterFillColor = checkIsClusterFillColor(symbol, classification.parameter);
    const currentSymbol = isClusterFillColor ? symbol : getExtractedSymbol(symbol, true);

    if (attribute || (isClusterFillColor && attributeName === CLUSTER_CLASSIFY_FILL_COLOR_ATTRIBUTE)) {
      const legendSection: LegendSectionProps = {
        baseSymbol: symbol,
        sectionTitle: attribute?.alias || attributeName,
        attributeName,
        attributeType,
        parameter: classification.parameter,
        items: [
          ...createLegendSectionItems(classification, classificationsParams, currentSymbol),
          createOtherLegendSection(classification, classificationsParams, currentSymbol, style.title || title),
        ],
      };

      if (isClusterFillColor) {
        legendSections.push(legendSection);
      } else {
        legendSections.unshift(legendSection);
      }
    }

    return legendSections;
  }, []);
}
